/**
 * A manual step on a run (AMO-307): what has to be done, the results that
 * reached the step, and, for a person it is for, Mark done with an optional
 * note. Shown beside the run chart and in the Inbox; the server checks again
 * who may.
 *
 * @module components/workflows/ChecklistDone
 */
import type { WorkflowRun } from "@amoreg/contracts";
import { CheckIcon } from "lucide-react";
import { useState } from "react";

import { refreshInbox } from "../../state/inbox";
import { decideWorkflowApproval } from "../../state/workflows";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { checklistStanding } from "./workflowCanvas.logic";

export function ChecklistDone({
  organizationId,
  run,
  nodeId,
  viewerEmail,
  onDone,
}: {
  readonly organizationId: string;
  readonly run: WorkflowRun;
  readonly nodeId: string;
  readonly viewerEmail: string | null | undefined;
  readonly onDone?: (run: WorkflowRun) => void;
}) {
  const node = run.graph.nodes.find((entry) => entry.id === nodeId);
  const step = run.steps.find((entry) => entry.nodeId === nodeId) ?? null;
  const standing = checklistStanding(run, nodeId, viewerEmail);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (node?.type !== "checklist") return null;
  const history = (run.decisions ?? []).filter((entry) => entry.nodeId === nodeId && entry.decision === "done");
  const outputs = new Map(run.steps.map((entry) => [entry.nodeId, entry.output]));
  const linked = run.graph.edges
    .filter((edge) => edge.target === nodeId)
    .map((edge) => ({ id: edge.source, label: run.graph.nodes.find((entry) => entry.id === edge.source)?.label ?? edge.source, output: outputs.get(edge.source) ?? null }));

  const markDone = async () => {
    if (!step) return;
    setBusy(true);
    setError(null);
    const outcome = await decideWorkflowApproval(organizationId, run.workflowId, run.id, nodeId, {
      attempt: step.attempt,
      decision: "done",
      ...(note.trim() ? { comment: note.trim() } : {}),
    });
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    setNote("");
    refreshInbox();
    onDone?.(outcome.value);
  };

  return (
    <div className="space-y-4" data-workflow-checklist={nodeId} data-workflow-checklist-standing={standing}>
      <div className="space-y-1">
        <p className="text-xs font-medium">What has to be done</p>
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">{node.config.instructions}</p>
      </div>
      {linked.map((source) =>
        source.output ? (
          <div key={source.id} className="space-y-1">
            <p className="text-xs font-medium">{source.label}</p>
            <div className="max-h-[30vh] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-[12.5px] leading-relaxed text-foreground">
              {source.output}
            </div>
          </div>
        ) : null,
      )}
      {standing === "do" ? (
        <div className="space-y-2" data-workflow-checklist-actions="">
          <Textarea
            rows={3}
            value={note}
            maxLength={5_000}
            placeholder="A note for the record (optional), e.g. Imported 42 trades; totals match."
            onChange={(event) => setNote(event.currentTarget.value)}
            data-workflow-checklist-note=""
          />
          <Button size="sm" onClick={() => void markDone()} disabled={busy} data-workflow-checklist-done="">
            <CheckIcon />
            {busy ? "Saving…" : "Mark done"}
          </Button>
          {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
        </div>
      ) : standing === "not-theirs" ? (
        <p className="text-[13px] text-muted-foreground">
          Waiting for {node.config.assigneeEmails.join(", ") || "the person who started the run"} to do it.
        </p>
      ) : null}
      {history.length > 0 ? (
        <div className="space-y-1.5" data-workflow-checklist-history="">
          {history.map((entry) => (
            <div key={`${entry.attempt}-${entry.decidedAt}`} className="rounded-md bg-muted/40 px-2.5 py-1.5 text-[12px]">
              <p>
                <span className="font-medium">Marked done</span>
                {entry.byEmail ? ` by ${entry.byEmail}` : ""}
                <span className="text-muted-foreground"> · {formatRelativeTimeLabel(entry.decidedAt)}</span>
              </p>
              {entry.comment ? <p className="whitespace-pre-wrap text-muted-foreground">{entry.comment}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
